import React, {useState} from 'react';
import {Card, CardContent, withStyles} from "@material-ui/core";
import Grid from "@material-ui/core/Grid/Grid";
import Typography from "@material-ui/core/Typography/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Table from "@material-ui/core/Table/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Checkbox from "@material-ui/core/Checkbox";
import {FormattedDate} from "react-intl";
import axios from "axios";
import swal from "sweetalert";

import i18n from '../../i18n';
import FormAutocomplete from "../Form/FormAutocomplete";

const formatDate = (date) => {
    const pad = (value) => (value < 10 ? '0' + value : '' + value);
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
};

const defaultRange = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - 30);
    return {from: formatDate(from), to: formatDate(to)};
};

const parseFileName = (disposition, fallback) => {
    if (!disposition) {
        return fallback;
    }
    const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
    if (!match || !match[1]) {
        return fallback;
    }
    try {
        return decodeURIComponent(match[1]);
    } catch (error) {
        return match[1];
    }
};

const ProtocolPrintSection = ({classes}) => {
    const initialRange = defaultRange();

    const [customer, setCustomer] = useState(null);
    const [machine, setMachine] = useState(null);
    const [from, setFrom] = useState(initialRange.from);
    const [to, setTo] = useState(initialRange.to);
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);

    const error = (key) => swal({icon: "error", title: i18n.get(key)});

    const handleSearch = () => {
        if (!customer) {
            error('dashboard.protocol_print_customer_required');
            return;
        }
        if (from > to) {
            error('dashboard.protocol_print_invalid_range');
            return;
        }

        const params = {customer_id: customer.value, from, to};
        if (machine) {
            params.machine_id = machine.value;
        }

        setLoading(true);
        axios.get('/protocols/print/search', {params})
            .then((response) => {
                setResults(response.data);
                setSelected([]);
                setSearched(true);
            })
            .catch(() => error('dashboard.protocol_print_error'))
            .then(() => setLoading(false));
    };

    const toggle = (id) => {
        setSelected(selected.indexOf(id) === -1
            ? selected.concat([id])
            : selected.filter((item) => item !== id));
    };

    const toggleAll = () => {
        setSelected(selected.length === results.length ? [] : results.map((item) => item.id));
    };

    const handlePrint = () => {
        if (selected.length === 0) {
            error('dashboard.protocol_print_select_required');
            return;
        }

        const payload = {protocol_ids: selected, customer_id: customer.value, from, to};
        if (machine) {
            payload.machine_id = machine.value;
        }

        setLoading(true);
        axios.post('/protocols/print/download', payload, {responseType: 'blob'})
            .then((response) => {
                const blob = new Blob([response.data], {type: 'application/zip'});
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = parseFileName(response.headers['content-disposition'], 'protokolle.zip');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch(() => error('dashboard.protocol_print_error'))
            .then(() => setLoading(false));
    };

    return (
        <Grid item xs={12}>
            <Card>
                <CardContent>
                    <Grid container spacing={8}>
                        <Grid item xs={12}>
                            <Typography variant="h6">
                                {i18n.get('dashboard.protocol_print')}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <FormAutocomplete
                                name="print_customer"
                                url="/search-board"
                                label={i18n.get('dashboard.protocol_print_customer')}
                                value={customer}
                                onChange={(option) => setCustomer(option)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormAutocomplete
                                name="print_machine"
                                url="/search-machine"
                                label={i18n.get('dashboard.protocol_print_machine')}
                                value={machine}
                                isClearable
                                onChange={(option) => setMachine(option)}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                type="date"
                                name="print_from"
                                fullWidth
                                label={i18n.get('dashboard.protocol_print_from')}
                                value={from}
                                onChange={(event) => setFrom(event.target.value)}
                                InputLabelProps={{shrink: true}}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                type="date"
                                name="print_to"
                                fullWidth
                                label={i18n.get('dashboard.protocol_print_to')}
                                value={to}
                                onChange={(event) => setTo(event.target.value)}
                                InputLabelProps={{shrink: true}}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={2} className={classes.searchCell}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                disabled={loading}
                                onClick={handleSearch}
                            >
                                {i18n.get('dashboard.protocol_print_search')}
                            </Button>
                        </Grid>

                        {searched &&
                        <Grid item xs={12}>
                            {results.length === 0 &&
                            <Typography>{i18n.get('dashboard.protocol_print_no_results')}</Typography>
                            }

                            {results.length > 0 &&
                            <React.Fragment>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={selected.length === results.length}
                                                    indeterminate={selected.length > 0 && selected.length < results.length}
                                                    onChange={toggleAll}
                                                />
                                            </TableCell>
                                            <TableCell>{i18n.get('dashboard.protocol_print_object')}</TableCell>
                                            <TableCell>{i18n.get('dashboard.protocol_print_editor')}</TableCell>
                                            <TableCell>{i18n.get('dashboard.protocol_print_date')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {results.map((protocol) => {
                                            const isSelected = selected.indexOf(protocol.id) !== -1;
                                            return (
                                            <TableRow
                                                key={protocol.id}
                                                hover
                                                selected={isSelected}
                                                style={{cursor: 'pointer'}}
                                                onClick={() => toggle(protocol.id)}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox color="primary" checked={isSelected}/>
                                                </TableCell>
                                                <TableCell>{protocol.object}</TableCell>
                                                <TableCell>{protocol.technician}</TableCell>
                                                <TableCell>
                                                    {protocol.created_at &&
                                                    <FormattedDate value={protocol.created_at}/>
                                                    }
                                                </TableCell>
                                            </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>

                                <div className={classes.printRow}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={loading || selected.length === 0}
                                        onClick={handlePrint}
                                    >
                                        {i18n.get('dashboard.protocol_print_download')}
                                    </Button>
                                </div>
                            </React.Fragment>
                            }
                        </Grid>
                        }
                    </Grid>
                </CardContent>
            </Card>
        </Grid>
    );
};

const styles = theme => ({
    searchCell: {
        display: 'flex',
        alignItems: 'flex-end',
    },
    printRow: {
        position: 'sticky',
        bottom: 0,
        marginTop: theme.spacing.unit * 2,
        paddingTop: theme.spacing.unit,
        paddingBottom: theme.spacing.unit,
        textAlign: 'right',
        backgroundColor: '#fff',
        zIndex: 1,
    },
});

export default withStyles(styles)(ProtocolPrintSection);



// WEBPACK FOOTER //
// ./resources/assets/js/components/Dashboard/ProtocolPrintSection.jsx